/**
 * Inventory Service
 * Manages equipment inventory operations
 */

const InventoryService = {
    // Data Keys
    INVENTORY_KEY: 'ecpv_inventory',

    init() {
        // Register in FileStorage if not already there
        if (typeof FileStorage !== 'undefined') {
            // Add mapping if it doesn't exist (it won't initially, needs manual registration or update to FileStorage)
            // But FileStorage structure is static in file-storage.js
            // We need to update file-storage.js to include these new keys
            console.log('InventoryService initialized');
        }
    },

    getAllItems() {
        return FileStorage.read(this.INVENTORY_KEY) || [];
    },

    getItemById(id) {
        const items = this.getAllItems();
        return items.find(i => i.id === id);
    },

    getItemsByCategory(category) {
        const items = this.getAllItems();
        return items.filter(i => i.category === category);
    },

    /**
     * @param {Object} itemData - { name, category, status, quantity, photo, allowedCourses }
     */
    async addItem(itemData) {
        const data={...itemData,quantity:Number(itemData.quantity),totalQuantity:Number(itemData.quantity)};
        const result=await WarehouseAPI.command('inventory','POST',{...data,id:WarehouseAPI.draftId('item',data)},r=>WarehouseAPI.cache('inventory',[r.item]));
        return result?.item || null;
    },

    async updateItem(id, updates) {
        const item=this.getItemById(id);
        if(!item)return null;
        const data={};
        for(const key of ['name','category','status','totalQuantity','description','serialNumber','photo','allowedCourses'])data[key]=updates[key] ?? item[key];
        const result=await WarehouseAPI.command('inventory/'+encodeURIComponent(id),'PUT',{...data,expectedUpdatedAt:item.updatedAt},r=>WarehouseAPI.cache('inventory',[r.item]));
        return result?.item || null;
    },

    async deleteItem(id) {
        const item=this.getItemById(id);if(!item)return false;
        return !!await WarehouseAPI.command('inventory/'+encodeURIComponent(id),'DELETE',{expectedUpdatedAt:item.updatedAt},()=>WarehouseAPI.cache('inventory',this.getAllItems().filter(i=>i.id!==id),true));
    },

    // Same list as CATEGORY_ICONS in server/security/warehouse.js.
    CATEGORY_ICONS: Object.freeze(['fa-box', 'fa-video', 'fa-camera', 'fa-eye', 'fa-lightbulb', 'fa-microphone', 'fa-headphones', 'fa-helicopter',
        'fa-desktop', 'fa-laptop', 'fa-ring', 'fa-tools', 'fa-cogs', 'fa-plug', 'fa-battery-full', 'fa-sd-card', 'fa-tshirt', 'fa-hat-cowboy',
        'fa-chair', 'fa-palette', 'fa-film', 'fa-music', 'fa-truck', 'fa-wrench']),

    // The school's categories (Almacén → Inventario → Categorías), loaded with the warehouse.
    // Hidden ones only label the items already in them; `all` includes them.
    getCategories(all = false) {
        const rows = (typeof FileStorage !== 'undefined' && FileStorage.dataStore?.warehouseCategories) || [];
        return rows.filter(c => all || c.active !== false)
            .map(c => ({ id: c.id, label: c.name, icon: c.icon || 'fa-box', active: c.active !== false, order: c.order, updatedAt: c.updatedAt }))
            // «Otros» (order 1000) stays last, also right after creating a new one.
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.label.localeCompare(b.label, 'es'));
    },

    // For lists and filters: the visible categories plus hidden ones that still have items.
    categoriesInUse(items = this.getAllItems()) {
        const used = new Set(items.map(i => i.category));
        return this.getCategories(true).filter(c => c.active || used.has(c.id));
    },

    async deleteCategory(id) {
        const current = this.getCategories(true).find(c => c.id === id);
        if (!current) return false;
        const rows = (FileStorage.dataStore.warehouseCategories || []).filter(c => c.id !== id);
        return !!await WarehouseAPI.command('categories/' + encodeURIComponent(id), 'DELETE', { expectedUpdatedAt: current.updatedAt },
            () => WarehouseAPI.cache('warehouseCategories', rows, true));
    },

    categoryLabel(id) {
        return this.getCategories(true).find(c => c.id === id)?.label || 'Otros';
    },

    async addCategory(data) {
        const result = await WarehouseAPI.command('categories', 'POST', data, r => WarehouseAPI.cache('warehouseCategories', [r.category]));
        return result?.category || null;
    },

    async updateCategory(id, changes) {
        const current = this.getCategories(true).find(c => c.id === id);
        if (!current) return null;
        const result = await WarehouseAPI.command('categories/' + encodeURIComponent(id), 'PUT', { ...changes, expectedUpdatedAt: current.updatedAt },
            r => WarehouseAPI.cache('warehouseCategories', [r.category]));
        return result?.category || null;
    },

    // Helper: Get available courses for permissions
    getCourses() {
        if (typeof FormationService !== 'undefined') {
            return FormationService.getFormations().map(f => ({
                id: f.id,
                label: f.name
            }));
        }
        return [];
    },

    /**
     * Import multiple items at once
     * @param {Array} itemsArray - Array of item objects
     * @returns {Promise<Array|null>} The created items, or null if it failed.
     */
    async importItems(itemsArray) {
        const items=itemsArray.map((item,index)=>{
            const data={name:item.name,category:item.category || 'other',status:'available',
                quantity:Number(item.quantity ?? 1),description:item.description || '',
                serialNumber:item.serialNumber || '',allowedCourses:[]};
            return {...data,id:WarehouseAPI.draftId('import-'+index,data)};
        });
        const result=await WarehouseAPI.command('inventory/import','POST',{items},r=>WarehouseAPI.cache('inventory',r.inventory));
        return result?result.inventory:null;
    },

    // What a spreadsheet (.xlsx or .csv, as base64) would create; nothing is written.
    async previewImport(file) {
        try {
            const response=await fetch('/api/warehouse/inventory/import/preview',{method:'POST',headers:{'Content-Type':'application/json','X-SICAF-CSRF':AuthManager.getCsrfToken()},body:JSON.stringify({file})});
            const data=await response.json().catch(()=>null);
            if(!response.ok || !data?.success)throw new Error(data?.error || 'No se pudo leer el archivo.');
            return data;
        } catch(e) {
            return {success:false,message:e.message || 'No se pudo conectar con el servidor.'};
        }
    },

    /**
     * Update allowed courses for an item
     */
    updateAllowedCourses(itemId, courseIds) {
        return this.updateItem(itemId, { allowedCourses: courseIds });
    },

    async uploadPhoto(file) {
        if (!file) return null;

        const formData = new FormData();
        // Rename file to avoid collisions/spaces: timestamp_filename
        const cleanName = Date.now() + '_' + file.name.replace(/[^a-zA-Z0-9.]/g, '_');

        // We can't append File directly in Node environment easily without browser API,
        // but this code runs in Browser.
        // Server expects raw binary or fetch with body.
        // Wait, server.js uses req.pipe(writeStream), so it expects raw stream for /upload?filename=...

        try {
            const token = AuthManager.getCsrfToken();
            const response = await fetch(`/upload?filename=${cleanName}&folder=almacen`, {
                method: 'POST',
                headers: {
                    'X-SICAF-CSRF': token
                },
                body: file
            });

            if (!response.ok) {
                throw new Error('Upload failed');
            }

            const result = await response.json();
            return result.path; // e.g., "uploads/almacen/123_image.jpg"
        } catch (error) {
            console.error('Error uploading photo:', error);
            return null;
        }
    },

    /**
     * Get items that have no courses assigned
     */
    getUnassignedItems() {
        const items = this.getAllItems();
        return items.filter(i => !i.allowedCourses || i.allowedCourses.length === 0);
    }
};

window.InventoryService = InventoryService;
